import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PhoneNumber } from 'src/components/input';

import { useDebounce } from '../../../_metronic/helpers';
import useTableWithItems from '../../../components/table/useTableWithItems';
import { useSearchContactsQuery } from '../../../reducers/contactApiSlice';
import AddGuests from '../reservationPages/AddGuests';
import GuestsTable from '../reservationPages/GuestsTable';

const ReservationGuests = ({ control, register, watch, errors, setValue }) => {
	// Watchers
	const people = watch('people');
	const phone = watch('host.phone');
	const hostId = watch('host._id');
	const tabolUserId = watch('host.tabol_user_id');
	const guestUsers = watch('guest_users') ?? [];

	const intl = useIntl();

	const isEmpty = (data) => {
		return typeof data === 'object'
			? Object.keys(data).length === 0 || Object.values(data).some((element) => element === undefined)
			: Array.isArray(data) && data.length === 0 && typeof data !== 'undefined';
	};

	const formatGuestList = (list, type) => {
		if (list && list.length > 0) {
			return list.map((guest) => {
				return {
					email: guest.email ?? '',
					firstname: type === 'contact' ? guest.fname : guest.firstname ?? '',
					lastname: type === 'contact' ? guest.lname : guest.lastname ?? '',
					id: guest._id ?? '',
					phoneNumber: type === 'contact' ? guest.phone : guest.phoneNumber,
					type: guest._id ? 'contact' : 'tabol_user',
				};
			});
		}
		return [];
	};

	// State
	const [editingGuest, setEditingGuest] = useState(null);
	const [searchTerm, setSearch] = useState('');
	const [openDropdown, setOpenDropdown] = useState('');
	const [guestList, setGuestList] = useState(!isEmpty(guestUsers) ? formatGuestList(guestUsers, 'contact') : [] ?? []);
	const [availableHours, setAvailableHours] = useState([]);
	const dropdownValue = openDropdown;
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	// Queries

	const args = debouncedSearchTerm.length >= 3 ? { query: { search: debouncedSearchTerm } } : skipToken;
	const { data: availableContacts = [], isSuccess: isContactSuccess } = useSearchContactsQuery(args);

	useEffect(() => {
		if (availableHours.length > 0) {
			if (hostId) {
				setValue('host.type', 'contact');
				setValue('host._id', hostId);
			} else {
				setValue('host.type', 'tabol_user');
				setValue('host._id', tabolUserId);
			}
		}

		if (!isEmpty(guestUsers)) {
			setGuestList(formatGuestList(guestUsers, 'contact') ?? []);
		}
	}, [availableHours]);

	const handleContactClick = (contact) => {
		setValue('host.fname', contact.fname, { shouldValidate: true });
		setValue('host.lname', contact.lname, { shouldValidate: true });
		setValue('host.email', contact.email, { shouldValidate: true });
		setValue('host.phone', contact.phone, { shouldValidate: true });
		setValue('host.type', 'contact');
		setValue('host._id', contact.id);
		setSearch('');
	};

	const handleUserClick = (user) => {
		setValue('host.fname', user.fname, { shouldValidate: true });
		setValue('host.lname', user.lname, { shouldValidate: true });
		setValue('host.email', user.email, { shouldValidate: true });
		setValue('host.phone', user.phone, { shouldValidate: true });
		setValue('host.type', 'tabol_user');
		setValue('host._id', user.id);
		setSearch('');
	};

	const handleEditGuest = (id) => {
		const guestToEdit = guestList.find((guest) => guest.id === id);
		setEditingGuest(guestToEdit);
	};

	const handleDeleteGuest = (id) => {
		setGuestList((prevGuestList) => {
			return prevGuestList.filter((guest) => guest.id !== id);
		});
	};

	const handleAddGuest = (guest, isUpdating) => {
		if (isUpdating) {
			const list = () => guestList.map((g) => (g._id === guest.id ? guest : g));
			setGuestList(list());
			setValue('guest_new_contact', list());
			if (guestUsers && guestUsers.length > 0) {
				setValue(`guest_users[${guestUsers.findIndex((g) => g.id === guest.id)}]`, guest);
			}
		} else {
			const list = () => [...guestList, guest];
			setGuestList(list());
			setValue('guest_new_contact', guestList);
			if (guest.type === 'new_contact') {
				setValue(
					'guest_new_contact',
					list().filter((item) => item.type === 'new_contact')
				);
			}
		}
	};

	const handleOpenDropdownChange = (value) => {
		setOpenDropdown(value);
	};

	let content = '';
	const tableValues = useMemo(() => {
		return {
			title: '',
			items: guestList,
			totalRows: guestList ? guestList.length : 0,
			content: <GuestsTable onEditGuest={handleEditGuest} onDeleteGuest={handleDeleteGuest} />,
		};
	}, [guestList]);

	const { table } = useTableWithItems(tableValues);
	if (Object.keys(guestList).length > 0) {
		content = table;
	}

	return (
		<>
			<div className="row pt-5">
				<div className="col-12">
					<div className="separator my-5" />
				</div>
			</div>
			<div className="row">
				<div className="col-12">
					<div className="pb-5 mb-0 fs-2 fw-bold">
						<FormattedMessage id="RESERVATION.DETAILS_OPTIONS.GUEST_DETAILS" />
					</div>
				</div>
			</div>
			<div className="row pb-4">
				<div className="col-12">
					<div className="input-group icon-input">
						<div className="custom-input-icon icon-left d-flex align-items-center px-6 fw-bold">
							<i className="fa-solid fa-magnifying-glass search-icon" />
						</div>
						<input
							type="search"
							className="form-control fs-6 p-4 ms-0 w-auto"
							data-role="tagsinput"
							placeholder={intl.formatMessage({ id: 'PLACEHOLDER.SEARCH_GUEST' })}
							value={searchTerm}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
					{isContactSuccess && (
						<div className="row">
							<div className="col-12">
								{availableContacts.contacts.length > 0 && (
									<div className="list-group">
										{availableContacts.contacts.map((contact) => (
											<div
												key={contact.id}
												className="list-group-item dropdown-item secondary-text list-group-item-action"
												onClick={() => handleContactClick(contact)}
											>
												{contact.name} - {contact.phone}
											</div>
										))}
									</div>
								)}
							</div>

							{availableContacts.users.length > 0 ? (
								<div className="list-group">
									<div className="row">
										<div className="col-12">
											<div className="separator my-5" />
										</div>
									</div>
									<h3>
										<FormattedMessage id="RESERVATION.DETAILS_OPTIONS.USERS" />
									</h3>
									{availableContacts.users.map((user) => (
										<div key={user.id} className="list-group-item list-group-item-action" onClick={() => handleUserClick(user)}>
											{user.fname} {user.lname} - {user.mobile}
										</div>
									))}
								</div>
							) : null}
						</div>
					)}
				</div>
			</div>

			<div className="p-3 generic-border">
				<div className="row">
					<div className="col-12 col-sm-6">
						<div className="input-group">
							<label htmlFor="guest-fname" className="pb-3 mb-0 fs-7 fw-bold required">
								<FormattedMessage id="RESERVATION.ADD_GUEST.FIRSTNAME" />
							</label>
							<input
								type="text"
								id="guest-fname"
								name="guest-fname"
								aria-labelledby="guest-fname"
								className="form-control fs-6 p-4"
								placeholder={intl.formatMessage({ id: 'PLACEHOLDER.FIRST_NAME' })}
								data-np-autofill-type="firstname"
								{...register('host.fname')}
							/>
							{errors?.host?.fname && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{errors.host.fname.message}</span>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="col-12 col-sm-6">
						<div className="input-group">
							<label htmlFor="guest-lname" className="pb-3 pt-5 pt-sm-0 fs-7 fw-bold required">
								<FormattedMessage id="RESERVATION.ADD_GUEST.LASTNAME" />
							</label>
							<input
								type="text"
								id="guest-lname"
								name="guest-lname"
								aria-labelledby="guest-lname"
								className="form-control fs-6 p-4"
								placeholder={intl.formatMessage({ id: 'PLACEHOLDER.LAST_NAME' })}
								data-np-autofill-type="lastname"
								{...register('host.lname')}
							/>
							{errors?.host?.lname && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{errors.host.lname.message}</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="row pt-5">
				<div className="col-12">
					<div className="row">
						<div className="col-12 col-sm-6">
							<div className="input-group">
								<label htmlFor="guest-email" className="pb-3 mb-0 fs-7 fw-bold">
									<FormattedMessage id="RESERVATION.ADD_GUEST.EMAIL" />
								</label>
								<input
									type="email"
									className="form-control p-4"
									id="guest-email"
									name="guest-email"
									aria-describedby="emailHelp"
									placeholder={intl.formatMessage({ id: 'PLACEHOLDER.EMAIL' })}
									{...register('host.email')}
								/>
								{errors?.host?.email && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{errors.host.email.message}</span>
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="col-12 col-sm-6">
							<div className="input-group">
								<PhoneNumber
									control={control}
									setValue={setValue}
									watch={watch}
									name="host.phone"
									errors={errors}
									register={register}
									title={intl.formatMessage({ id: 'RESERVATION.ADD_GUEST.PHONENUMBER' })}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row pt-5">
				<div className="col-12">
					<div className="separator my-5" />
				</div>
			</div>

			<div className="row pb-5">
				<div className="col-12">
					<div className="fs-2 fw-bold mb-0">
						<FormattedMessage id="RESERVATION.DETAILS_OPTIONS.OTHER_GUESTS" />
					</div>
				</div>
			</div>

			{content}
			<AddGuests
				onAddGuest={handleAddGuest}
				people={people - 1}
				guestList={guestList}
				openDropdownValue={dropdownValue}
				onOpenDropdownChange={handleOpenDropdownChange}
				editingGuest={editingGuest}
				setEditingGuest={setEditingGuest}
				setValue={setValue}
			/>
			<div className="row">
				<div className="col-12">
					<div className="separator my-5" />
				</div>
			</div>
		</>
	);
};

export default ReservationGuests;
